import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { BORDER_RADIUS } from "app/theme";
import {
  formatTitle,
  getGroupKeyForCategory,
} from "features/programmesActions/ActionList";
import { useActionsByCategory } from "../hooks/useInsightsDashboardQueries";

// Match the action status colours used in the farmer detail views.
const STATUS_COLORS = {
  completed: "#15803d",
  onTrack: "#0369a1",
  planned: "#e4e4e7",
} as const;

type GroupTotals = {
  key: string;
  committed: number;
  onTrack: number;
  completed: number;
};

function StackedBar({
  committed,
  onTrack,
  completed,
  max,
  height = 10,
}: {
  committed: number;
  onTrack: number;
  completed: number;
  max: number;
  height?: number;
}) {
  const planned = Math.max(committed - onTrack - completed, 0);
  const widthPct = max > 0 ? (committed / max) * 100 : 0;
  const seg = (n: number) =>
    committed > 0 ? `${(n / committed) * 100}%` : "0%";
  return (
    // No full-width track — the bar's length itself encodes the action count,
    // so categories with fewer actions render as shorter bars.
    <Box sx={{ height, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: `${widthPct}%`,
          minWidth: widthPct > 0 ? height : 0,
          borderRadius: height / 2,
          overflow: "hidden",
          transition: "width 0.6s ease",
        }}
      >
        <Box
          sx={{
            width: seg(completed),
            backgroundColor: STATUS_COLORS.completed,
          }}
        />
        <Box
          sx={{ width: seg(onTrack), backgroundColor: STATUS_COLORS.onTrack }}
        />
        <Box
          sx={{ width: seg(planned), backgroundColor: STATUS_COLORS.planned }}
        />
      </Box>
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      <Typography variant="caption" sx={{ color: "#71717a" }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function ActionMomentum({
  farmCount = 0,
}: {
  /** Number of farms with an agreed action plan, for the headline. */
  farmCount?: number;
}) {
  const { data: byCategory, isLoading: categoriesLoading } =
    useActionsByCategory();

  if (categoriesLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={180}
        sx={{ borderRadius: `${BORDER_RADIUS.medium}px` }}
      />
    );
  }

  const groups = new Map<string, GroupTotals>();
  let committed = 0;
  let onTrack = 0;
  let completed = 0;
  for (const row of byCategory?.data ?? []) {
    const categoryId = Number(row["SubmittedActions.categoryId"]);
    const key = getGroupKeyForCategory(categoryId);
    if (!key) continue;
    const entry = groups.get(key) ?? {
      key,
      committed: 0,
      onTrack: 0,
      completed: 0,
    };
    entry.committed += Number(row["SubmittedActions.uniqueActionCount"]) || 0;
    entry.onTrack += Number(row["SubmittedActions.uniqueActionsOnTrack"]) || 0;
    entry.completed +=
      Number(row["SubmittedActions.uniqueActionsCompleted"]) || 0;
    groups.set(key, entry);
    committed += Number(row["SubmittedActions.uniqueActionCount"]) || 0;
    onTrack += Number(row["SubmittedActions.uniqueActionsOnTrack"]) || 0;
    completed += Number(row["SubmittedActions.uniqueActionsCompleted"]) || 0;
  }
  const sortedGroups = Array.from(groups.values()).sort(
    (a, b) => b.committed - a.committed
  );
  const maxGroupCommitted = sortedGroups[0]?.committed ?? 0;

  if (committed === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#71717a" }}>
        No actions committed yet — totals will appear here once farmers agree
        their farm action plans.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Overall progress */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#3f3f46" }}>
            <Box
              component="span"
              sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#18181b" }}
            >
              {committed}
            </Box>{" "}
            actions committed
            {farmCount > 0
              ? ` across ${farmCount} farm${farmCount === 1 ? "" : "s"}`
              : " across the programme"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <LegendDot
              color={STATUS_COLORS.completed}
              label={`${completed} completed`}
            />
            <LegendDot
              color={STATUS_COLORS.onTrack}
              label={`${onTrack} on track`}
            />
            <LegendDot
              color={STATUS_COLORS.planned}
              label={`${Math.max(committed - onTrack - completed, 0)} planned`}
            />
          </Box>
        </Box>
        <StackedBar
          committed={committed}
          onTrack={onTrack}
          completed={completed}
          max={committed}
          height={12}
        />
      </Box>

      {/* By category */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {sortedGroups.map((group) => (
          <Box
            key={group.key}
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(140px, 220px) 1fr auto",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#3f3f46",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatTitle(group.key)}
            </Typography>
            <Tooltip
              title={`${group.completed} completed · ${group.onTrack} on track · ${group.committed} committed`}
              arrow
              placement="top"
            >
              <Box>
                <StackedBar
                  committed={group.committed}
                  onTrack={group.onTrack}
                  completed={group.completed}
                  max={maxGroupCommitted}
                />
              </Box>
            </Tooltip>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#18181b", textAlign: "right" }}
            >
              {group.committed}
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "#a1a1aa", ml: 0.5 }}
              >
                actions
              </Typography>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
